import { TailgateGeometrySidecarI } from '../types/tailgateGeometrySidecar'

export interface ContainedContentRectI {
  height: number
  offsetX: number
  offsetY: number
  scale: number
  width: number
}

export interface TailgateZoneCountsI {
  secured: number
  unsecured: number
}

/**
 * Cumulative zone-entry counts per frame index — how many unique tracks have
 * entered each zone so far. The numbers only ever increment (ENG-34102).
 *
 * Raw "unique ids ever seen in the zone" over-counts badly: the tracker's ids
 * churn (one walking person can carry half a dozen ids across a clip), so
 * every re-acquisition inside a zone would bump the counter. Both counts
 * therefore apply the evaluator's own directionality filter
 * (person_count_evaluator.py): a track whose FIRST zoned appearance is the
 * secured zone is an exit or a churned re-acquisition, never an entry.
 *
 * - unsecured: unique tracks whose first zoned appearance is the unsecured
 *   zone, counted at that frame. (Within one frame the unsecured zone wins,
 *   matching the collector's membership evaluation order.)
 * - secured: completed unsecured→secured entries — exactly what the sidecar's
 *   `crossings` field records, one per track, at the crossing frame.
 *
 * Residual limit: a track re-acquired in the unsecured zone on the way OUT
 * still counts as a new unsecured entry. That is only fixable with stabler
 * ids from the evaluator, not client-side.
 */
export const getCumulativeZoneCounts = (
  frames: TailgateGeometrySidecarI['frames'],
  crossings: TailgateGeometrySidecarI['crossings']
): TailgateZoneCountsI[] => {
  const crossingsByFrame = new Map<number, number>()
  crossings.forEach(({ frame }) => {
    crossingsByFrame.set(frame, (crossingsByFrame.get(frame) ?? 0) + 1)
  })

  const classifiedIds = new Set<number>()
  let secured = 0
  let unsecured = 0

  return frames.map((tracks, frameIndex) => {
    tracks.forEach((track) => {
      if (classifiedIds.has(track.id)) return
      if (track.in_unsecured) {
        classifiedIds.add(track.id)
        unsecured += 1
      } else if (track.in_secured) {
        // First seen in the secured zone: an exit or a re-acquired id.
        classifiedIds.add(track.id)
      }
    })
    secured += crossingsByFrame.get(frameIndex) ?? 0
    return { secured, unsecured }
  })
}

export const getTailgateFrameIndex = (
  mediaTime: number,
  fps: number,
  frameCount: number
) => {
  if (frameCount <= 0 || fps <= 0 || mediaTime < 0) return null
  return Math.min(frameCount - 1, Math.max(0, Math.round(mediaTime * fps)))
}

export const getContainedContentRect = (
  containerWidth: number,
  containerHeight: number,
  frameWidth: number,
  frameHeight: number
): ContainedContentRectI | null => {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    frameWidth <= 0 ||
    frameHeight <= 0
  ) {
    return null
  }

  const scale = Math.min(
    containerWidth / frameWidth,
    containerHeight / frameHeight
  )
  const width = frameWidth * scale
  const height = frameHeight * scale

  return {
    height,
    offsetX: (containerWidth - width) / 2,
    offsetY: (containerHeight - height) / 2,
    scale,
    width,
  }
}
